/**
 * Created by Simon Zhao on 9/8/2022.
 * Description:
 * NoteDepositTriggerFilterDialog Component
 * ------ maintenance history ------
 */
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, filter, map, take } from 'rxjs/operators';
import { businessConstants } from '../../../../constants/business.constants';
import { ConditionFieldItem, DiagramConditionModel, fieldOperatorMap } from '../../../../models/workflow-condition.model';
import { DEFAULT_METDATA_OF_NOTE } from '../../../../tamalelibs/models/automation.model';
import { EntityBrief } from '../../../../tamalelibs/models/entity-brief.model';
import { EntityService } from '../../../../tamalelibs/services/entity.service';
import { AlertWindowService } from '../../../../widgets/alert-window/alert-window.service';
import { FilterActionViewModel } from '../../../../widgets/diagram/diagram-conditional-gateway-dialog/diagram-conditional-gateway-dialog.component';
import { NoteTriggerFilterDialogOptions, NoteTriggerFilterDialogResult } from './note-deposit-trigger-filter-dialog.model';
import { NoteDepositTiggerFilterDialogService } from './note-deposit-trigger-filter-dialog.service';
@Component({
    selector: 'tam-note-deposit-trigger-filter-dialog',
    templateUrl: './note-deposit-trigger-filter-dialog.component.html',
    styleUrls: ['./note-deposit-trigger-filter-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NoteDepositTriggerFilterDialogComponent implements OnInit, OnDestroy {
    //#region public properties
    currentEntityCandidates: Array<EntityBrief> = new Array<EntityBrief>();

    filterFieldCandidates: Array<ConditionFieldItem> = [];

    opened: boolean;

    rowItems: Array<DiagramConditionModel> = [];

    isEntityListLoading: boolean;

    get isConditionsPristine(): boolean {
        if (this.isConditionConfigValid()) {
            const conditionString = this.rowItems.length === 0 ? '' : DiagramConditionModel.export(this.rowItems, '');
            return conditionString === this._initialConditionString;
        } else if (DiagramConditionModel.isConditionBlankEquivalent(this.rowItems)) {
            // only one blank row exists, if the initial condition is blank, the status is pristine.
            return DiagramConditionModel.isConditionBlank(this._initialConditionString);
        }

        return false;
    }

    isSaveBtnClicked = false;

    //#endregion

    //#region private properties

    /**
    * the subscription array that would be unsubscribed on destroying.
    */
    private _destroySubscriptions: Array<Subscription> = [];

    private _filterSubscription;

    private _filterSubject$: Subject<FilterActionViewModel> = new Subject();

    private _initialConditionString = '';

    //#endregion

    constructor(private _changeDetectorRef: ChangeDetectorRef, private _dialogService: NoteDepositTiggerFilterDialogService,
        private _entityService: EntityService, private _alertWindow: AlertWindowService) { }

    //#region public methods

    ngOnInit() {
        this._destroySubscriptions.push(
            this._dialogService.dialogOpen$.pipe(filter(action => !this.opened),
            ).subscribe((dialogOptions: NoteTriggerFilterDialogOptions) => {
                if (dialogOptions.fields && dialogOptions.fields.length > 0) {
                    dialogOptions.fields.forEach(fld => {
                        const fldCandidate = new ConditionFieldItem();
                        fldCandidate.fieldId = fld.attrDefID;
                        fldCandidate.displayText = fld.lableName;
                        fldCandidate.fieldType = fld.fieldType;
                        fldCandidate.fieldConfig = fld;
                        this.filterFieldCandidates.push(fldCandidate);
                    });
                }
                DEFAULT_METDATA_OF_NOTE.forEach(meta => {
                    const fldCandidate = new ConditionFieldItem();
                        fldCandidate.fieldId = meta.fieldInTemplate;
                        fldCandidate.displayText = meta.nameOfFieldInTemplate;
                        fldCandidate.fieldType = meta.fieldType as 'entity' | 'text' | 'number' | 'date';
                        fldCandidate.fieldConfig = meta;
                        this.filterFieldCandidates.push(fldCandidate);
                });
                // Keep date-typed fields and special fields(note body and attachment) from filter candidate list.
                const filterFieldBlacklist = ['Display Date', 'Attachment', 'Note Body'];
                this.filterFieldCandidates = this.filterFieldCandidates.filter(fld => fld.fieldType !== 'date' && !filterFieldBlacklist.includes(fld.displayText));
                this._initialConditionString = DiagramConditionModel.isConditionBlank(dialogOptions.condition) ? '' : dialogOptions.condition;
                let existingRows;
                if (dialogOptions.filters && dialogOptions.filters.length > 0) {
                    existingRows = [...dialogOptions.filters];
                } else {
                    existingRows = DiagramConditionModel.isConditionBlank(dialogOptions.condition) ? [] : DiagramConditionModel.import(dialogOptions.condition);
                }
                const rowNum = existingRows.length > 0 ? existingRows.length : 1;
                for (let i = 0; i < rowNum; i++) {
                    // create row placeholders for existing rows. default to create a blank row if no existing row found.
                    const initRow = new DiagramConditionModel();
                    initRow.fieldCandidates = this.filterFieldCandidates;
                    this.rowItems.push(initRow);
                }
                this._loadExistingRows(existingRows, dialogOptions.fields);
                this._filterSubscription = this._filterSubject$.pipe(
                    debounceTime(250),
                ).subscribe((action: FilterActionViewModel) => {
                    if (action.keywords) {
                        action.row.fieldCandidates = this.filterFieldCandidates.filter(item =>
                            item.displayText.toLowerCase().indexOf(action.keywords.toLowerCase()) > -1
                        );
                    } else {
                        action.row.fieldCandidates = [... this.filterFieldCandidates];
                    }
                });
                this.opened = true;
                this._changeDetectorRef.detectChanges();
                console.log(dialogOptions);
            }));
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._destroySubscriptions = [];
    }

    onAddFilterRow(e: any) {
        const newRow = new DiagramConditionModel();
        newRow.fieldCandidates = this.filterFieldCandidates;
        this.rowItems.push(newRow);
        this.isSaveBtnClicked = false;
    }

    onFieldSelectionChanged(row: DiagramConditionModel, selection: ConditionFieldItem) {
        if (selection) {
            const operators = !fieldOperatorMap[selection.fieldType] ? fieldOperatorMap['text'] : fieldOperatorMap[selection.fieldType];
            row.operatorCandidates = operators;
            row.fieldType = selection.fieldType;
            row.fieldConfig = selection.fieldConfig;
            row.expectedValue = null;
            row.operator = null;
            this.isSaveBtnClicked = false;
        }
    }

    onCancelBtnClick($event: Event) {
        if ($event.stopPropagation) {
            $event.stopPropagation();
        }

        if (!this.isConditionsPristine) {
            const subscription = this._alertWindow.warn(businessConstants.alertWindow.warnTitle, [businessConstants.alertWindow.warnContent])
                .subscribe((result: boolean) => {
                    if (result) {
                        this._close();
                    }
                    subscription.unsubscribe();
                });
        } else {
            this._close();
        }
    }

    onClickSaveBtn(event: Event) {
        if (event) {
            event.stopPropagation();
        }

        this.isSaveBtnClicked = true;
        if (this.isConditionConfigValid()) {
            this._close(this.rowItems);
        } else if (DiagramConditionModel.isConditionBlankEquivalent(this.rowItems)) {
            // saving with a blank row.
            this._close([]);
        }
    }

    onDeleteFilterRow(row: DiagramConditionModel) {
        this.rowItems.splice(this.rowItems.indexOf(row), 1);
        this.isSaveBtnClicked = false;
    }

    onEntityListFilterChanged(ks: string) {
        // update the candidate list accordingly.
        this._entityService.getEntityListBySearchTextQuick(1, 50, ks).pipe(
            catchError((e) => {
                return of(null);
            }),
            take(1),
        ).subscribe(res => {
            this.currentEntityCandidates = this._entityService.mapEntityBriefList(res);
            this.isEntityListLoading = false;
            this.isSaveBtnClicked = false;
            this._changeDetectorRef.markForCheck();
        });
    }

    isConditionConfigValid(): boolean {
        const isConditionInvalidFunc = (condition: DiagramConditionModel) => {
            // expectedValue is a little special, besides the empty string, an empty array is also treated as null value for this field.
            return !condition || !condition.fieldId || !condition.operator || (!condition.expectedValue && condition.expectedValue !== 0) || (Array.isArray(condition.expectedValue) && Array.from(condition.expectedValue).length === 0);
        };

        return this.rowItems && !this.rowItems.some(row => isConditionInvalidFunc(row));
    }
    //#endregion

    //#region private methods
    private _close(conditions: DiagramConditionModel[] = null) {
        this.opened = false;
        this._changeDetectorRef.detectChanges();
        const dialogResult = new NoteTriggerFilterDialogResult();
        dialogResult.isConfirmed = !!conditions;
        if (conditions) {
            dialogResult.conditionResult = DiagramConditionModel.export(conditions, '');
            dialogResult.filterResult = [...conditions];
        } else {
            dialogResult.conditionResult = '';
            dialogResult.filterResult = [];
        }

        this._dialogService.dialogClose$.next(dialogResult);
        if (this._filterSubscription) {
            this._filterSubscription.unsubscribe();
        }
        this._reset();
    }

    private _reset() {
        this.rowItems = [];
        this.filterFieldCandidates = [];
        this.isSaveBtnClicked = false;
    }

    private _loadExistingRows(existingRows: Array<any>, fieldDefs: Array<any>) {
        if (existingRows.length > 0) {
            const entityIdMap = {};
            existingRows.forEach(row => {
                if (fieldOperatorMap.entity.findIndex(op => op.key === row.operator.key) >= 0) {
                    // an entity row.
                    Array.from(row.expectedValue).map((entityObj: { id: string }) => entityObj.id).forEach((entityId: string) => {
                        if (!entityIdMap[entityId]) {
                            entityIdMap[entityId] = true;
                        }
                    });
                }

            });

            const entityIdArray = Object.keys(entityIdMap);
            if (entityIdArray.length > 0) {
                this._destroySubscriptions.push(this._entityService.getEntityListByIdsQuick(entityIdArray).pipe(
                    map(response => this._entityService.mapEntityBriefList(response))).subscribe(
                        (entityList: Array<EntityBrief>) => {
                            existingRows.forEach(row => {
                                if (fieldOperatorMap.entity.findIndex(op => op.key === row.operator.key) >= 0) {
                                    Array.from(row.expectedValue).forEach((entityObj: { id: string, shortName?: string }) => {
                                        const entityData = entityList.find(en => en.id === entityObj.id);
                                        if (entityData) {
                                            // update the display text for the matched entity.
                                            entityObj.shortName = entityData.shortName;
                                        }
                                    });
                                }
                            });

                            this._changeDetectorRef.detectChanges();
                        }
                    )
                );
            }

            // execute the population of existing data after the candidate list is ready.
            setTimeout(
                () => {
                    this.rowItems.forEach((row: DiagramConditionModel, index: number) => {
                        row.fieldId = existingRows[index].fieldId;
                        this.onFieldSelectionChanged(row, row.fieldCandidates.find(fld => fld.fieldId === row.fieldId.fieldId));
                        row.operator = existingRows[index].operator;
                        row.expectedValue = existingRows[index].expectedValue;
                        row.fieldConfig = fieldDefs.find(fld => fld.fieldDefinitionId === row.fieldId.fieldId);
                    });
                    this._changeDetectorRef.detectChanges();
                }
            );
        }
    }
    //#endregion
}
